import {
  LowIncomeSubsidyOption,
  LisStatus,
  type TranslationKey,
  type UseTranslateTypeStrict,
} from "../@types";
import { getYearOrMostRecentValue } from "./constantsHelpers";

type LISStatusLabelsType = {
  [key: string]: {
    [key in LisStatus]: TranslationKey;
  };
};

/**
 * lowIncomeSubsidyStatusLabels define phrase keys for a given year and LIS level.
 */
export const lowIncomeSubsidyStatusLabels: LISStatusLabelsType = {
  "2025": {
    [LisStatus.Level1A]: "csr_landing.lis.2025.level1A",
    [LisStatus.Level1A_DN]: "csr_landing.lis.2025.level1A",
    [LisStatus.Level1B]: "na",
    [LisStatus.Level1C]: "csr_landing.lis.2025.level1A",
    [LisStatus.Level2]: "csr_landing.lis.2025.level2",
    [LisStatus.Level3]: "csr_landing.lis.level3",
    [LisStatus.NoHelp]: "csr_landing.lis.no_help",
  },
  "2026": {
    [LisStatus.Level1A]: "csr_landing.lis.2026.level1A",
    [LisStatus.Level1A_DN]: "csr_landing.lis.2026.level1A",
    [LisStatus.Level1B]: "na",
    [LisStatus.Level1C]: "csr_landing.lis.2026.level1A",
    [LisStatus.Level2]: "csr_landing.lis.2026.level2",
    [LisStatus.Level3]: "csr_landing.lis.level3",
    [LisStatus.NoHelp]: "csr_landing.lis.no_help",
  },
  "2027": {
    [LisStatus.Level1A]: "csr_landing.lis.2027.level1",
    [LisStatus.Level1A_DN]: "csr_landing.lis.2027.level1",
    [LisStatus.Level1B]: "na",
    [LisStatus.Level1C]: "csr_landing.lis.2027.level1",
    [LisStatus.Level2]: "csr_landing.lis.2027.level2",
    [LisStatus.Level3]: "csr_landing.lis.level3",
    [LisStatus.NoHelp]: "csr_landing.lis.no_help",
  },
} as const;

/**
 * Typesafe helper for returning LIS- and year-specific labels
 * @example getLowIncomeSubsidyStatusLabelText({level: lis_level, year, t})
 * @returns the translated label for the given year and LIS level, or the most year's
 * label for the given LIS level, if the year passed in isn't yet defined.
 *
 * If used where you need to conditionally render something, add additional checks,
 * as this helper always returns a string.
 */
export const getLowIncomeSubsidyStatusLabelText = ({
  level,
  t,
  year,
}: {
  level: LisStatus;
  t: UseTranslateTypeStrict;
  year: string | number;
}): string =>
  t(getYearOrMostRecentValue(year, lowIncomeSubsidyStatusLabels)[level]);

export const isLowIncomeSubsidyStatusLabelAvailable = (
  year: string | number,
  level: LisStatus
): boolean => {
  if (lowIncomeSubsidyStatusLabels[`${year}`]) {
    return lowIncomeSubsidyStatusLabels[`${year}`][level] ? true : false;
  } else {
    return false;
  }
};

export const getNewLISCodeFromUserOptions = (
  helpType: LowIncomeSubsidyOption | undefined
): LisStatus => {
  if (helpType === LowIncomeSubsidyOption.Medicaid) {
    return LisStatus.Level1A;
  }
  if (helpType === LowIncomeSubsidyOption.SSI) {
    return LisStatus.Level1A_DN;
  }
  if (helpType === LowIncomeSubsidyOption.MSP) {
    return LisStatus.Level1B;
  }
  if (helpType === LowIncomeSubsidyOption.Unsure) {
    return LisStatus.NoHelp;
  }
  if (helpType === LowIncomeSubsidyOption.SS) {
    return LisStatus.Level1C;
  }
  return LisStatus.NoHelp;
};

export const getUserOptionFromLISCode = (
  lis?: LisStatus
): LowIncomeSubsidyOption | undefined => {
  switch (lis) {
    case LisStatus.Level1A: {
      return LowIncomeSubsidyOption.Medicaid;
    }
    case LisStatus.Level1A_DN: {
      return LowIncomeSubsidyOption.SSI;
    }
    case LisStatus.Level1B: {
      return LowIncomeSubsidyOption.MSP;
    }
    case LisStatus.Level1C: {
      return LowIncomeSubsidyOption.SS;
    }
    case LisStatus.NoHelp: {
      return LowIncomeSubsidyOption.None;
    }
  }
  return undefined;
};

/**
 * Returns true if the LIS level indicates Dual Special Needs Plan -
 * Has both Medicare and Medicaid.
 */
export const isDSNP = (lis: LisStatus | undefined): boolean =>
  !!lis &&
  [LisStatus.Level1A, LisStatus.Level2, LisStatus.Level3].includes(lis);

/**
 * @param date Should be in the format YYYY-MM-DD
 */
export const getPlanYearForFutureLisStartDate = (date = ""): number | null => {
  const isDateFormat = /\d\d\d\d-\d\d-\d\d/.test(date);
  if (!isDateFormat) {
    return null;
  }
  return +date.slice(0, 4);
};
